import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";

export function RecentBookingChanges() {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [ackingId, setAckingId] = useState(null);
  const canView = hasPerm("bookings.view");
  const canAck = hasPerm("bookings.update");

  const load = useCallback(() => {
    api.get("/bookings/revisions/recent?limit=8").then(({ data }) => setRows(data || [])).catch(() => {});
  }, []);
  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView || rows.length === 0) return null;

  const acknowledge = async (bookingId) => {
    setAckingId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/acknowledge-revision`);
      toast.success("Change acknowledged");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to acknowledge");
    } finally { setAckingId(null); }
  };

  return (
    <Card className="border-[#E8E8E6]" data-testid="recent-booking-changes-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-[#1B4332]" /> Recent booking changes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.map((r, i) => (
          <div key={`${r.booking_id}-${r.at}-${i}`}
               className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${r.acknowledged ? "border-[#E8E8E6] bg-white" : "border-amber-200 bg-amber-50/60"}`}
               data-testid={`booking-change-row-${i}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1C1F1E]">
                {r.visitor_name}
                <span className="font-normal text-[#525C58]"> — revised by {r.by_name}</span>
              </p>
              <p className="text-xs text-[#525C58]">{(r.changes || []).join(" · ")}</p>
              <p className="text-[10px] text-[#7A8A84] mt-0.5 flex items-center gap-1">
                <Clock className="h-3 w-3" /> {r.at ? new Date(r.at).toLocaleString() : ""}
                {r.acknowledged && r.ack_by_name && <span>· acknowledged by {r.ack_by_name}</span>}
              </p>
            </div>
            {r.acknowledged ? (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] shrink-0"
                     data-testid={`change-acked-${i}`}>
                <BadgeCheck className="h-3 w-3 mr-1" /> Acknowledged
              </Badge>
            ) : canAck ? (
              <Button size="sm" onClick={() => acknowledge(r.booking_id)} disabled={ackingId === r.booking_id}
                      className="bg-amber-600 hover:bg-amber-700 h-7 text-xs shrink-0"
                      data-testid={`ack-change-btn-${i}`}>
                <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Acknowledge
              </Button>
            ) : (
              <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] shrink-0">
                Awaiting acknowledgement
              </Badge>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
